import { TENANT_ROLES, TENANT_ROLE_DESCRIPTIONS, TENANT_ROLE_LABELS } from '../../../shared/solution.mjs';
import { GUARDIAN_STATUS_LABEL } from './watch-format.js';

// Guardian is shown as a role and is never written to the IdP (spec, "Guardian as a role"):
// this id exists only in the browser.
export const GUARDIAN_ROLE = 'guardian';
export const ROLE_IDS = Object.freeze([...TENANT_ROLES, GUARDIAN_ROLE]);
export const ROLE_LABELS = Object.freeze({ ...TENANT_ROLE_LABELS, [GUARDIAN_ROLE]: 'Guardian' });
export const ROLE_DESCRIPTIONS = Object.freeze({
  ...TENANT_ROLE_DESCRIPTIONS,
  [GUARDIAN_ROLE]: 'Receives Watch notices and hears of every change to Watch. At most 10.',
});

// The status of a staff member pre-assigned and not yet signed in.
export const PENDING = 'pending-first-sign-in';

const lower = (s) => String(s ?? '').trim().toLowerCase();

export const isStaffEmail = (email) => /@([a-z0-9-]+\.)*ipc-global\.com$/i.test(email ?? '');

// A member People can change: active, or a pending staff pre-assignment; an
// IPC Global staff row is one only when it was pre-assigned through People.
export const manageable = (m) => (m.status === 'active' || m.status === PENDING) && (!isStaffEmail(m.email) || m.preAssigned === true);

export const guardianTone = (status) => (status === 'valid' ? 'green' : status === 'pending' ? 'amber' : 'red');

export function guardianChip(g) {
  return { id: GUARDIAN_ROLE, label: 'Guardian', tone: guardianTone(g.status), title: GUARDIAN_STATUS_LABEL[g.status] ?? g.status };
}

export function originOf(member) {
  if (member.status === PENDING && !member.account) return 'SSO';
  if (member.account?.federated) return 'SSO';
  if (member.account) return 'Invite';
  return isStaffEmail(member.email) ? 'SSO' : 'Invite';
}

export function statusOf(member) {
  if (member.status === 'revoked') return { key: 'revoked', label: 'Revoked', tone: 'red' };
  if (member.status === PENDING) return { key: 'pending', label: 'Pending first sign-in', tone: 'amber' };
  if (member.account?.enabled === false) return { key: 'disabled', label: 'Disabled', tone: 'red' };
  if (member.account?.status === 'FORCE_CHANGE_PASSWORD') return { key: 'invited', label: 'Invited', tone: 'amber' };
  return { key: 'active', label: 'Active', tone: 'green' };
}

/**
 * One row per person. `members` is GET /people, `guardians` is
 * GET /watch/guardians (null when the caller may not read Watch content).
 * A guardian matches a member by directory address, or by the typed lookup
 * until the address is pinned; a guardian nobody matches is a Directory row.
 */
export function buildPeopleRows({ members, guardians }) {
  const byKey = new Map();
  for (const g of guardians ?? []) {
    if (g.lookup) byKey.set(lower(g.lookup), g);
    if (g.address) byKey.set(lower(g.address), g);
  }
  const matched = new Set();
  const rows = (members ?? []).map((m) => {
    const g = byKey.get(lower(m.email)) ?? null;
    if (g) matched.add(g.guardianId);
    const tenantRoles = TENANT_ROLES.filter((r) => (m.roles ?? []).includes(r));
    const roles = [...tenantRoles.map((r) => ({ id: r, label: ROLE_LABELS[r], tone: 'slate' })), ...(g ? [guardianChip(g)] : [])];
    return { id: `member:${lower(m.email)}`, kind: 'member', email: m.email, name: g?.displayName ?? null, member: m, guardian: g, tenantRoles, roles, origin: originOf(m), status: statusOf(m) };
  });
  for (const g of guardians ?? []) {
    if (matched.has(g.guardianId)) continue;
    rows.push({
      id: `directory:${g.guardianId}`,
      kind: 'directory',
      email: g.address ?? g.lookup,
      name: g.displayName ?? null,
      member: null,
      guardian: g,
      tenantRoles: [],
      roles: [guardianChip(g)],
      origin: 'Directory',
      status: { key: g.status, label: GUARDIAN_STATUS_LABEL[g.status] ?? g.status, tone: guardianTone(g.status) },
    });
  }
  return rows;
}

export const membersOfRole = (rows, roleId) => rows.filter((r) => r.roles.some((c) => c.id === roleId));

export const peopleSearchText = (row) => [row.name, row.email, ...row.roles.map((r) => r.label), row.origin, row.status.label].filter(Boolean).join(' ');
